/**
 * Cart Bundles
 * Handles cart bundle interactions: expand/collapse, quantity management, duplicate, remove
 */

class CartBundles {
  constructor() {
    this.bundles = document.querySelectorAll('[data-bundle-group]');
    this.init();
  }

  /**
   * Initializes bundle interactions
   */
  init() {
    this.setupToggleHandlers();
    this.setupDropdownHandlers();
    this.setupActionHandlers();
    this.setupQuantityHandlers();
    this.setupMiniCartListener();
    this.restoreOpenAccordion();
    this.convertOptionUnits();
  }

  /**
   * Sets up MutationObserver to watch mini cart for content changes
   */
  setupMiniCartListener() {
    const miniCart = document.querySelector('mini-cart');
    if (!miniCart) return;

    const observer = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some(mutation =>
        mutation.type === 'childList' && mutation.addedNodes.length > 0
      );

      if (hasRelevantChanges) {
        this.convertOptionUnits();
      }
    });

    observer.observe(miniCart, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Converts mm values to inches in bundle option text for items with inches unit preference.
   * Finds all bundle options containers with data-unit-preference="inches" and converts
   * measurement patterns like "40mm" to "1.57"" in the <dd> text content.
   */
  convertOptionUnits() {
    const UNIT_RATIO = 25.4;
    const MEASUREMENT_PATTERN = /(\d+(?:\.\d+)?)\s*mm\b/gi;

    const optionsContainers = document.querySelectorAll('.cart-line__bundle-options[data-unit-preference="inches"]');

    optionsContainers.forEach(container => {
      const ddElements = container.querySelectorAll('dd');
      ddElements.forEach(dd => {
        if (dd.dataset.unitConverted) return;

        const childNodes = Array.from(dd.childNodes);
        childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE && MEASUREMENT_PATTERN.test(node.textContent)) {
            node.textContent = node.textContent.replace(MEASUREMENT_PATTERN, (match, value) => {
              const mmValue = parseFloat(value);
              const inchValue = (mmValue / UNIT_RATIO).toFixed(2);
              return `${inchValue}"`;
            });
          }
        });

        dd.dataset.unitConverted = 'true';
      });
    });
  }

  /**
   * Restores accordion open state after page reload or DOM update
   */
  restoreOpenAccordion() {
    const openGroupId = sessionStorage.getItem('bundleAccordionOpen');
    if (openGroupId) {
      sessionStorage.removeItem('bundleAccordionOpen');
      const button = document.querySelector(`[data-bundle-edit-toggle="${openGroupId}"]`);
      if (button) {
        this.toggleEditContent(openGroupId, button);
      }
    }
    this.convertOptionUnits();
  }

  /**
   * Waits for mini cart DOM to update then restores accordion state and converts units
   * Uses polling to reliably detect when new content has loaded
   * @param {HTMLElement} minicart - The mini cart element
   */
  waitForMiniCartUpdate(minicart) {
    const openGroupId = sessionStorage.getItem('bundleAccordionOpen');

    let attempts = 0;
    const maxAttempts = 30;
    const pollInterval = 100;

    const checkForContent = () => {
      attempts++;

      const isLoading = minicart.querySelector('.mini-cart-wrap.loading');
      const hasContent = minicart.querySelector('.cart-line__bundle-options');

      if (!isLoading && hasContent) {
        if (openGroupId) {
          this.restoreOpenAccordion();
        } else {
          this.convertOptionUnits();
        }
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkForContent, pollInterval);
      }
    };

    setTimeout(checkForContent, 150);
  }

  /**
   * Saves which accordion is open before cart update
   * @param {string} groupId - Bundle GroupId
   */
  saveOpenAccordion(groupId) {
    sessionStorage.setItem('bundleAccordionOpen', groupId);
  }

  /**
   * Sets up expand/collapse handlers for edit accordion, addons, and bespoke options
   */
  setupToggleHandlers() {
    document.addEventListener('click', (event) => {
      const editToggle = event.target.closest('[data-bundle-edit-toggle]');
      if (editToggle) {
        event.preventDefault();
        const groupId = editToggle.dataset.bundleEditToggle;
        this.toggleEditContent(groupId, editToggle);
      }

      const addonsToggle = event.target.closest('[data-bundle-addons-toggle]');
      if (addonsToggle) {
        event.preventDefault();
        const groupId = addonsToggle.dataset.bundleAddonsToggle;
        this.toggleAddons(groupId, addonsToggle);
      }

      const bespokeToggle = event.target.closest('.cart-line__bespoke-toggle');
      if (bespokeToggle) {
        event.preventDefault();
        this.toggleBespoke(bespokeToggle);
      }
    });
  }

  /**
   * Sets up dropdown menu handlers
   */
  setupDropdownHandlers() {
    document.addEventListener('click', (event) => {
      const dropdownToggle = event.target.closest('.cart-bundle__dropdown-toggle');

      if (dropdownToggle) {
        event.stopPropagation();
        const menu = dropdownToggle.nextElementSibling;
        const isOpen = !menu.hidden;

        this.closeAllDropdowns();

        if (!isOpen) {
          menu.hidden = false;
        }
      } else {
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * Sets up bundle action handlers (edit, remove, optional item remove)
   */
  setupActionHandlers() {
    document.addEventListener('click', (event) => {
      const editButton = event.target.closest('[data-bundle-edit]');
      const removeButton = event.target.closest('[data-bundle-remove]');
      const removeOptionalButton = event.target.closest('[data-remove-optional]');

      if (editButton) {
        event.preventDefault();
        const { bundleEdit: groupId, handle } = editButton.dataset;
        this.handleEdit(groupId, handle);
      }

      /* PRESERVED - Duplicate handler commented out for potential future use
      const duplicateButton = event.target.closest('[data-bundle-duplicate]');
      if (duplicateButton) {
        event.preventDefault();
        const { bundleDuplicate: groupId, handle } = duplicateButton.dataset;
        this.handleDuplicate(groupId, handle);
      }
      */

      if (removeButton) {
        event.preventDefault();
        const { bundleRemove: groupId, groupIds } = removeButton.dataset;
        this.handleRemove(groupId, groupIds, removeButton);
      }

      if (removeOptionalButton) {
        event.preventDefault();
        const itemKey = removeOptionalButton.dataset.removeOptional;
        this.handleOptionalRemove(itemKey, removeOptionalButton);
      }
    });
  }

  /**
   * Sets up quantity input handlers for bundle items
   */
  setupQuantityHandlers() {
    document.addEventListener('change', (event) => {
      const input = event.target.closest('.cart-line__bundle-item .cart-line__qty-input');
      if (!input) return;

      const itemKey = input.dataset.itemKey;
      const groupId = input.dataset.groupId;
      const newQty = parseInt(input.value, 10);
      const isTlp = input.dataset.isTlp === 'true';
      const isRequired = input.dataset.isRequired === 'true';
      const isOptional = input.dataset.isOptional === 'true';

      if (isTlp || isRequired) {
        this.handleSyncedQuantityChange(groupId, newQty, input);
      } else if (isOptional) {
        this.handleOptionalQuantityChange(itemKey, newQty, input);
      }
    });
  }

  /**
   * Toggles bundle edit accordion expansion
   * @param {string} groupId - Bundle GroupId
   * @param {HTMLElement} button - Toggle button element
   */
  toggleEditContent(groupId, button) {
    const content = document.querySelector(`[data-bundle-edit-content="${groupId}"]`);
    if (!content) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', !isExpanded);

    /* Update button text */
    const textEl = button.querySelector('span');
    if (textEl) {
      textEl.textContent = isExpanded
        ? button.dataset.showText
        : button.dataset.hideText;
    }

    if (isExpanded) {
      content.classList.remove('is-open');
      content.addEventListener('transitionend', function handler() {
        content.removeEventListener('transitionend', handler);
        if (!content.classList.contains('is-open')) {
          content.hidden = true;
        }
      });
    } else {
      content.hidden = false;
      /* Double rAF ensures browser paints collapsed state before expanding */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          content.classList.add('is-open');
        });
      });
    }
  }

  /**
   * Toggles bundle addons expansion with animated grid
   * @param {string} groupId - Bundle GroupId
   * @param {HTMLElement} button - Toggle button element
   */
  toggleAddons(groupId, button) {
    const content = document.querySelector(`[data-bundle-addons-content="${groupId}"]`);
    if (!content) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      content.classList.remove('is-open');
    } else {
      content.hidden = false;
      requestAnimationFrame(() => content.classList.add('is-open'));
    }

    const toggleText = button.querySelector('.cart-line__addons-toggle-text');
    if (toggleText) {
      toggleText.textContent = isExpanded
        ? toggleText.dataset.showText
        : toggleText.dataset.hideText;
    }
  }

  /**
   * Toggles bespoke options visibility
   * @param {HTMLElement} button - Toggle button element
   */
  toggleBespoke(button) {
    const container = button.closest('.cart-line__bespoke');
    if (!container) return;

    const content = container.querySelector('.cart-line__bespoke-content');
    if (!content) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      content.classList.remove('is-open');
    } else {
      content.hidden = false;
      requestAnimationFrame(() => content.classList.add('is-open'));
    }

    const textEl = button.querySelector('span');
    if (textEl) {
      textEl.textContent = isExpanded
        ? button.dataset.showText
        : button.dataset.hideText;
    }
  }

  /**
   * Closes all dropdown menus
   */
  closeAllDropdowns() {
    const menus = document.querySelectorAll('.cart-bundle__dropdown-menu');
    menus.forEach(menu => {
      menu.hidden = true;
    });
  }

  /**
   * Handles quantity change for synced items (TLP + required add-ons)
   * @param {string} groupId - Bundle GroupId
   * @param {number} newQty - New quantity value
   * @param {HTMLElement} triggerInput - The input that triggered the change
   */
  async handleSyncedQuantityChange(groupId, newQty, triggerInput) {
    const bundleLine = triggerInput.closest('.cart-line');

    if (newQty === 0) {
      const groupIds = this.collectBundleKeys(groupId);
      return this.handleRemove(groupId, groupIds.join(' '), triggerInput);
    }

    const updates = {};
    const tlpKey = triggerInput.dataset.itemKey;
    if (tlpKey) {
      updates[tlpKey] = newQty;
    }

    const requiredItems = document.querySelectorAll(
      `.cart-line__bundle-item[data-group-id="${groupId}"][data-is-required="true"]`
    );
    requiredItems.forEach(item => {
      const itemKey = item.dataset.itemKey;
      if (itemKey) {
        updates[itemKey] = newQty;
      }
    });

    await this.updateCart(updates, bundleLine, triggerInput);
  }

  /**
   * Handles quantity change for optional add-ons (independent)
   * @param {string} itemKey - Cart item key
   * @param {number} newQty - New quantity value
   * @param {HTMLElement} triggerInput - The input that triggered the change
   */
  async handleOptionalQuantityChange(itemKey, newQty, triggerInput) {
    const bundleLine = triggerInput.closest('.cart-line');

    if (newQty === 0) {
      return this.handleOptionalRemove(itemKey, triggerInput);
    }

    const updates = { [itemKey]: newQty };
    await this.updateCart(updates, bundleLine, triggerInput);
  }

  /**
   * Handles removal of an optional add-on
   * @param {string} itemKey - Cart item key
   * @param {HTMLElement} triggerElement - The element that triggered the removal
   */
  async handleOptionalRemove(itemKey, triggerElement) {
    const bundleLine = triggerElement.closest('.cart-line');
    const updates = { [itemKey]: 0 };
    await this.updateCart(updates, bundleLine, triggerElement);
  }

  /**
   * Collects all cart item keys for a bundle
   * @param {string} groupId - Bundle GroupId
   * @returns {Array} Array of cart item keys
   */
  collectBundleKeys(groupId) {
    const items = document.querySelectorAll(
      `.cart-line__bundle-item[data-group-id="${groupId}"]`
    );
    const keys = [];
    items.forEach(item => {
      const key = item.dataset.itemKey;
      if (key) keys.push(key);
    });
    return keys;
  }

  /**
   * Updates cart via API and refreshes UI
   * @param {Object} updates - Object mapping item keys to quantities
   * @param {HTMLElement} bundleLine - The bundle line element
   * @param {HTMLElement} triggerElement - The element that triggered the update
   */
  async updateCart(updates, bundleLine, triggerElement) {
    if (bundleLine) bundleLine.classList.add('cart-line--loading');

    const minicart = document.querySelector('mini-cart');
    const isInMiniCart = triggerElement && triggerElement.closest('.cart-line--mini');
    if (isInMiniCart && minicart) {
      const miniCartWrap = minicart.querySelector('.mini-cart-wrap');
      if (miniCartWrap) miniCartWrap.classList.add('loading');
    }

    const groupId = triggerElement?.dataset?.groupId;
    if (groupId) {
      this.saveOpenAccordion(groupId);
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        if (isInMiniCart && minicart) {
          this.waitForMiniCartUpdate(minicart);
          minicart.updateSectionContents();
        } else {
          window.location.reload();
        }
      } else {
        sessionStorage.removeItem('bundleAccordionOpen');
        this.clearLoadingState(bundleLine, isInMiniCart, minicart);
      }
    } catch (error) {
      sessionStorage.removeItem('bundleAccordionOpen');
      this.clearLoadingState(bundleLine, isInMiniCart, minicart);
    }
  }

  /**
   * Clears loading state from bundle line and mini cart
   * @param {HTMLElement} bundleLine - The bundle line element
   * @param {boolean} isInMiniCart - Whether the action was in mini cart
   * @param {HTMLElement} minicart - The mini cart element
   */
  clearLoadingState(bundleLine, isInMiniCart, minicart) {
    if (bundleLine) bundleLine.classList.remove('cart-line--loading');
    if (isInMiniCart && minicart) {
      const miniCartWrap = minicart.querySelector('.mini-cart-wrap');
      if (miniCartWrap) miniCartWrap.classList.remove('loading');
    }
  }

  /**
   * Validates that bundle quantities are in sync
   * @param {string} groupId - Bundle GroupId
   * @returns {boolean} True if valid, false if quantities are out of sync
   */
  validateBundleQuantities(groupId) {
    const tlpInput = document.querySelector(
      `.cart-line__bundle-item[data-group-id="${groupId}"] .cart-line__qty-input[data-is-tlp="true"]`
    );
    if (!tlpInput) return true;

    const tlpQty = parseInt(tlpInput.value, 10);

    const requiredInputs = document.querySelectorAll(
      `.cart-line__bundle-item[data-group-id="${groupId}"] .cart-line__qty-input[data-is-required="true"]`
    );

    for (const input of requiredInputs) {
      const qty = parseInt(input.value, 10);
      if (qty !== tlpQty) return false;
    }

    return true;
  }

  /**
   * Sets checkout button disabled state and shows/hides error message
   * @param {boolean} disabled - Whether to disable checkout
   * @param {string} errorMessage - Error message to display (optional)
   */
  setCheckoutDisabled(disabled, errorMessage = '') {
    const checkoutButtons = document.querySelectorAll('[data-checkout-button]');
    const errorContainers = document.querySelectorAll('[data-bundle-error]');

    checkoutButtons.forEach(button => {
      button.disabled = disabled;
      if (disabled) {
        button.classList.add('button--disabled');
      } else {
        button.classList.remove('button--disabled');
      }
    });

    errorContainers.forEach(container => {
      if (disabled && errorMessage) {
        container.textContent = errorMessage;
        container.hidden = false;
      } else {
        container.hidden = true;
      }
    });
  }

  /**
   * Handles edit bundle action by redirecting to product page with bundle data
   * @param {string} groupId - Bundle GroupId
   * @param {string} handle - Product handle
   */
  async handleEdit(groupId, handle) {
    const bundleData = await this.collectBundleData(groupId, 'edit');
    if (!bundleData) return;

    sessionStorage.setItem('editBundle', JSON.stringify(bundleData));
    window.location.href = `/products/${handle}`;
  }

  /**
   * PRESERVED - NOT CURRENTLY USED
   *
   * Handles duplicate bundle action by redirecting to product page with bundle data.
   * Preserved for potential future use if duplicate functionality is needed.
   *
   * To re-enable: Add data-bundle-duplicate button in templates and uncomment
   * the handler in setupActionHandlers.
   *
   * @param {string} groupId - Bundle GroupId
   * @param {string} handle - Product handle
   */
  async handleDuplicate(groupId, handle) {
    const bundleData = await this.collectBundleData(groupId, 'duplicate');
    if (!bundleData) return;

    sessionStorage.setItem('editBundle', JSON.stringify(bundleData));
    window.location.href = `/products/${handle}`;
  }

  /**
   * Handles remove bundle action
   * @param {string} groupId - Bundle GroupId
   * @param {string} groupIds - Space-separated cart item keys
   * @param {HTMLElement} triggerElement - The button that triggered the remove
   */
  async handleRemove(groupId, groupIds, triggerElement) {
    const keys = groupIds.split(' ').filter(key => key.trim() !== '');
    const updates = Object.fromEntries(keys.map(key => [key, 0]));

    const bundleLine = triggerElement ? triggerElement.closest('.cart-line') : null;
    if (bundleLine) bundleLine.classList.add('cart-line--loading');

    const minicart = document.querySelector('mini-cart');
    const isInMiniCart = triggerElement && triggerElement.closest('.cart-line--mini');
    if (isInMiniCart && minicart) {
      const miniCartWrap = minicart.querySelector('.mini-cart-wrap');
      if (miniCartWrap) miniCartWrap.classList.add('loading');
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        if (isInMiniCart && minicart) {
          minicart.updateSectionContents();
        } else {
          window.location.reload();
        }
      }
    } catch (error) {
      if (bundleLine) bundleLine.classList.remove('cart-line--loading');
      if (isInMiniCart && minicart) {
        const miniCartWrap = minicart.querySelector('.mini-cart-wrap');
        if (miniCartWrap) miniCartWrap.classList.remove('loading');
      }
    }
  }

  /**
   * Filters out internal/system properties from bespoke options
   * Keeps user-facing values including confirmation checkbox values ("Yes")
   * @param {Object} properties - Item properties object
   * @returns {Object} Filtered properties
   */
  filterBespokeProperties(properties) {
    const systemKeys = ['GroupId', 'groupedProduct', 'required', 'min', 'aog', 'tlp', 'hasOptions', 'next_day'];
    return Object.fromEntries(
      Object.entries(properties).filter(([key]) => {
        const lowerKey = key.toLowerCase();
        return !systemKeys.includes(key) &&
          !key.startsWith('_') &&
          !lowerKey.startsWith('bespoke');
      })
    );
  }

  /**
   * Transforms cart item to selection format
   * @param {Object} item - Cart item
   * @returns {Object} Selection object
   */
  transformBundleItem(item) {
    const bespokeOptions = this.filterBespokeProperties(item.properties);
    return {
      variantId: item.variant_id,
      quantity: item.quantity,
      isRequired: item.properties.required === 'true',
      price: item.price,
      productTitle: item.product_title,
      variantTitle: item.variant_title,
      bespokeOptions: Object.keys(bespokeOptions).length > 0 ? bespokeOptions : null
    };
  }

  /**
   * Builds selections object from bundle items
   * @param {Array} bundleItems - Array of cart items
   * @returns {Object} Selections grouped by category
   */
  buildSelections(bundleItems) {
    return bundleItems
      .filter(item => item.properties?.groupedProduct === 'true' && item.properties?.tlp !== 'true')
      .reduce((selections, item) => {
        const categoryId = item.properties.aog ?? 'unknown';
        return {
          ...selections,
          [categoryId]: {
            ...(selections[categoryId] || {}),
            [item.variant_id]: this.transformBundleItem(item)
          }
        };
      }, {});
  }

  /**
   * Collects bundle data for duplicate action
   * @param {string} groupId - Bundle GroupId
   * @param {string} type - Action type ('duplicate')
   * @returns {Promise<Object|null>} Bundle data object
   */
  async collectBundleData(groupId, type) {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await cartResponse.json();

      const bundleItems = cart.items.filter(item =>
        item.properties?.GroupId === groupId
      );

      if (bundleItems.length === 0) return null;

      const tlpItems = bundleItems.filter(item => item.properties?.tlp === 'true');
      if (tlpItems.length === 0) return null;

      const selections = this.buildSelections(bundleItems);
      const tlpBespokeProperties = this.filterBespokeProperties(tlpItems[0].properties);

      return {
        type,
        groupId,
        productHandle: tlpItems[0].handle,
        tlpVariants: tlpItems.map(item => ({
          variantId: item.variant_id,
          quantity: item.quantity
        })),
        tlpBespokeProperties: Object.keys(tlpBespokeProperties).length > 0 ? tlpBespokeProperties : null,
        selections
      };
    } catch (error) {
      console.error('Error collecting bundle data:', error);
      return null;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartBundles();
});
